// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  AltoIcon,
  Body,
  Button,
  Column,
  Description,
  LgPadding,
  ListItem,
  SmPadding,
} from '@alto/design-system';
import copy from 'copy-to-clipboard';
import React, { useContext, useState } from 'react';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSelectorShared } from '~shared/store';
import referralImage from '~web/images/inviteFriends@2x.png';

const emailSubject = 'Have you tried Alto Pharmacy?';

const emailBody = (referral: string) => `
Hi!
%0D%0A%0D%0A
I've been using Alto Pharmacy to get my medications delivered to my door for free. They have everything I need in stock and can help with insurance and copays. It's saved me so much valuable time.
%0D%0A%0D%0A
Switch to a better pharmacy: ${referral}
`;

export const ReferralActionSheet = () => {
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();
  const [copied, setCopied] = useState(false);
  const currentUser = useSelectorShared(getCurrentUser);
  const referralLink = currentUser?.referral_link ?? 'https://alto.com';

  const emailPrompt = `mailto:?subject=${emailSubject}&body=${emailBody(referralLink)}`;
  const onEmailPrompt = () => {
    trackEvent({
      event: EVENTS.REFERRAL_LINK_SHARED,
      params: {
        shareMethod: 'email',
      },
    });
    window.open(emailPrompt, '_blank');
  };

  const copyLink = () => {
    copy(referralLink);
    setCopied(true);
    trackEvent({
      event: EVENTS.REFERRAL_LINK_SHARED,
      params: {
        shareMethod: 'copy',
      },
    });

    // FIXME: this is sloppy
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  return (
    <ActionSheetV2
      title="Invite a Friend to Alto"
      analyticsName="referral"
      illustrationSrc={referralImage}
      illustrationAccessibilityLabel="Friends with arms around each other"
      buttons={[
        <Button
          label="Send email"
          accessibilityLabel="Press to send email to refer a friend"
          onPress={onEmailPrompt}
          key="send-email-referral"
        />,
        <Button
          label="Cancel"
          accessibilityLabel="Cancel"
          type="tertiary"
          key="cancel-referral"
          onPress={closeActionSheet}
        />,
      ]}
    >
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <Column
          centerHorizontally
          gap="SM"
        >
          <SmPadding>
            <Body>Share this unique code to invite your friends and family to Alto:</Body>
            <ListItem
              title="Copy link"
              onPress={copyLink}
              LeftContent={
                <AltoIcon
                  testID="copy-referral-link"
                  name="copy-small"
                  type="disabled"
                />
              }
              descriptions={<Description>{referralLink}</Description>}
            />
            {copied ? <Description>Copied!</Description> : <Description>&nbsp;</Description>}
          </SmPadding>
        </Column>
      </LgPadding>
    </ActionSheetV2>
  );
};
